/*!
 * Copyright 2022 Thorne Health Lab. All rights reserved.
 */

import React, { PureComponent } from 'react'
import { bindActionCreators } from 'redux'
import { connect } from 'react-redux'
import injectSheet from 'react-jss'
import PropTypes from 'prop-types'
// eslint-disable-next-line
import { size, find } from 'lodash'
import Button from '../../components/Button'
import Spinner from '../../components/Spinner'
import Demographics from './ReportComponents/Demographics'
import BodyComposition from './ReportComponents/BodyComposition'
import BodySymmetry from './ReportComponents/BodySymmetry'
import Cognition from './ReportComponents/Cognition'
import Laboratory from './ReportComponents/Laboratory'
import Dexterity from './ReportComponents/Dexterity'
import PhysicalActivity from './ReportComponents/PhysicalActivity'
import Strength from './ReportComponents/Strength'
import Balance from './ReportComponents/Balance'
import Anthropometrics from './ReportComponents/Anthropometrics'
import Nutrition from './ReportComponents/Nutrition'
import {
  setProgressBarPercent,
  setActiveVisit,
  setActiveReport,
  apiRequest,
  highlightNavbarSection,
  changeRoute
} from '../../root/actions'
import { dispatch } from '../../root/store'
import { VISIT_URL } from '../../api/endpoints'
import style from './style'

export class Report extends PureComponent {
  constructor(props) {
    super(props)
    this.state = {
      currentRoute: '',
      ReportComponents: {
        Demographics,
        BodyComposition,
        BodySymmetry,
        Cognition,
        Laboratory,
        Dexterity,
        PhysicalActivity,
        Strength,
        Balance,
        Anthropometrics,
        Nutrition
      }
    }
  }

  // When subscribed props update check if we are on the correct report
  componentDidUpdate() {
    const hasRefreshedReport = this.state.currentRoute === this.props.match.url
    if (!hasRefreshedReport) {
      this.setState({ currentRoute: this.props.match.url }, () => {
        this.refreshReport()
      })
    }
  }

  componentWillUnmount() {
    // Reset progress bar when done looking at reports.
    dispatch(setProgressBarPercent(0))
  }

  isReportLoaded() {
    return (
      this.props.activeReport &&
      this.props.activeReport._meta.originalTitle ===
        this.props.match.params.reportId
    )
  }

  isVisitLoaded() {
    return (
      this.props.activeVisit &&
      this.props.activeVisit.uuid === this.props.match.params.visitId
    )
  }

  calculateReportProgress(activeReport) {
    const totalReports = size(this.props.activeReports) - 1
    const activeReportIndex = activeReport._meta.index
    const percent = Math.abs((activeReportIndex / totalReports) * 100 - 0.01)
    return percent
  }

  onRefreshReportSuccess(activeReport) {
    dispatch(setProgressBarPercent(this.calculateReportProgress(activeReport)))
    const mainContent = document.querySelector('.main-content')
    if (mainContent) mainContent.scrollTop = 0
  }
  // Load a new report state depending route params
  refreshReport() {
    const { visitId, reportId } = this.props.match.params

    if (!this.isVisitLoaded()) {
      const request = {
        url: `${VISIT_URL}/${visitId}`,
        method: 'get'
      }

      const activeVisitMeta = {
        setActiveReport: true,
        activeReport: reportId
      }

      const meta = {
        Component: 'Report',
        assert: {
          item: visitId,
          test: 'isString'
        },
        onSuccess: visit => {
          dispatch(setActiveVisit(visit, activeVisitMeta))

          const isCurrentVisit =
            this.props.currentVisit !== null &&
            this.props.currentVisit.uuid === visit.data.data.uuid

          // Set the sidebar to the appropriate section depending if we are the currentVisit or not.
          if (isCurrentVisit) {
            dispatch(highlightNavbarSection('currentReport'))
          } else {
            dispatch(highlightNavbarSection('history'))
          }

          this.onRefreshReportSuccess(this.props.activeReports[reportId])
        }
      }

      dispatch(apiRequest(request, meta))
    } else if (!this.isReportLoaded()) {
      dispatch(setActiveReport(this.props.activeReports[reportId]))
      this.onRefreshReportSuccess(this.props.activeReports[reportId])
    }
  }

  componentDidMount() {
    if (!this.isReportLoaded() || !this.isVisitLoaded()) this.refreshReport()
  }
  render() {
    if (!this.isReportLoaded() || !this.isVisitLoaded()) {
      return <Spinner />
    }

    const { classes } = this.props
    const ComponentName = this.props.activeReport._meta.Component
    const ReportComponent = this.state.ReportComponents[ComponentName]
    const activeReportIndex = this.props.activeReport._meta.index

    const nextReportIndex =
      activeReportIndex < size(this.props.activeReports) - 1
        ? this.props.activeReport._meta.index + 1
        : 0
    const backReportIndex =
      activeReportIndex > 0
        ? this.props.activeReport._meta.index - 1
        : size(this.props.activeReports) - 1

    const nextReport = find(this.props.activeReports, {
      _meta: { index: nextReportIndex }
    })
    const backReport = find(this.props.activeReports, {
      _meta: { index: backReportIndex }
    })

    const onNextBtnClick = () => {
      this.props.changeRoute({ route: nextReport._meta.route })
    }
    const onBackBtnClick = () => {
      this.props.changeRoute({ route: backReport._meta.route })
    }

    return (
      <div className={classes.base}>
        <div>
          {!this.props.isMobile && (
            <h2>{this.props.activeReport._meta.title}</h2>
          )}
          <ul className="active category">
            <li className="active category">
              <div className="cat-container">
                <div className="content">
                  <div className="viz">
                    <ReportComponent
                      details={this.props.activeReport}
                      isMobile={this.props.isMobile}
                      hotload
                    />
                  </div>
                </div>
              </div>
            </li>
          </ul>
        </div>
        {this.props.isMobile && (
          <div className="twoButtons">
            <Button fixed onClick={onBackBtnClick}>
              Back
            </Button>
            <Button fixedRight onClick={onNextBtnClick}>
              Next
            </Button>
          </div>
        )}
      </div>
    )
  }
}

const mapStateToProps = state => {
  return {
    activeVisit: state.containers.app.get('activeVisit'),
    currentVisit: state.containers.app.get('currentVisit'),
    activeReports: state.containers.app.get('activeReports'),
    activeReport: state.containers.app.get('activeReport'),
    isMobile: state.containers.app.get('isMobile')
  }
}

const mapDispatchToProps = dispatch => {
  return bindActionCreators({ changeRoute }, dispatch)
}

Report.propTypes = {
  activeVisit: PropTypes.object,
  currentVisit: PropTypes.object,
  activeReports: PropTypes.object,
  activeReport: PropTypes.object,
  isMobile: PropTypes.bool,
  match: PropTypes.object,
  changeRoute: PropTypes.func,
  classes: PropTypes.object
}

export default injectSheet(() => style)(
  connect(
    mapStateToProps,
    mapDispatchToProps
  )(Report)
)
